import * as p from '@clack/prompts'
import type { NeonProvisionResult, VercelRunner } from '@cli-types/next-adapter/vercel.js'
import { TERMS_ACCEPTED_PATTERN } from '@cli-constants/patterns.js'
import { INTERACTIVE_PROVISION_TIMEOUT_MS, PROVISION_TIMEOUT_MS } from '@cli-constants/timeouts.js'
import { clackLogRows, eraseRows, spinner } from '@cli-utils/terminal.js'
import pc from 'picocolors'
import { runVercel } from '../runner/run-vercel.js'
import { findAcceptTermsUrl } from './find-accept-terms-url.js'
import { neonAddArgs } from './neon-add-args.js'
import { readNeonProvisionInfo } from './read-neon-provision-info.js'

/**
 * On a fresh account the quiet attempt opens a browser and waits for the marketplace terms to be
 * accepted; its captured output — and therefore the wait — would otherwise be invisible. Callers
 * must stop any active spinner first: the terminal rerun's inherited stdio fights a clack spinner.
 */
export async function provisionNeonInteractive(
  runner: VercelRunner,
  cwd: string,
  options: { plan?: string; env?: NodeJS.ProcessEnv }
): Promise<NeonProvisionResult> {
  const quietSpinner = spinner()
  quietSpinner.start('Creating your Neon database')
  let termsNotice: { text: string; rows: number } | undefined
  let termsResolved = false

  // clack has no way to restyle a printed notice, so the block is erased and reprinted as a
  // settled `log.step` once the user accepts.
  const settleTermsNotice = () => {
    if (!termsNotice || termsResolved) return
    termsResolved = true
    quietSpinner.clear()
    eraseRows(termsNotice.rows)
    p.log.step(termsNotice.text)
  }

  const quiet = await runVercel(runner, neonAddArgs(options, 'json'), {
    cwd,
    mode: 'capture',
    timeoutMs: PROVISION_TIMEOUT_MS,
    env: options.env,
    onOutputLine: (line, run) => {
      if (!termsNotice) {
        const termsUrl = findAcceptTermsUrl(line)
        if (!termsUrl) return
        run.extendTimeout(INTERACTIVE_PROVISION_TIMEOUT_MS)
        const message = 'Please accept the Neon terms in your browser'
        // Guide row + message row + URL row(s); both clackLogRows calls count
        // the shared guide row, so drop one.
        termsNotice = {
          text: `${message}\n${pc.dim(termsUrl)}`,
          rows: clackLogRows(message) + clackLogRows(termsUrl) - 1
        }
        quietSpinner.clear()
        p.log.info(termsNotice.text)
        quietSpinner.start('Waiting for the terms to be accepted')
        return
      }
      if (TERMS_ACCEPTED_PATTERN.test(line)) {
        settleTermsNotice()
        quietSpinner.start('Creating your Neon database')
      }
    }
  })

  if (quiet.success) {
    settleTermsNotice()
    quietSpinner.clear()
    return readNeonProvisionInfo(quiet.stdout)
  }

  // The rerun inherits stdio, which fights a running spinner.
  quietSpinner.clear()
  p.log.info('Create your Neon database in the Vercel prompts below')

  const add = await runVercel(runner, neonAddArgs(options, 'interactive'), {
    cwd,
    mode: 'inherit',
    timeoutMs: INTERACTIVE_PROVISION_TIMEOUT_MS,
    env: options.env
  })

  if (!add.success) {
    return { failure: add.timedOut ? 'timeout' : 'provision-failed' }
  }

  return { usedTerminalFallback: true }
}
